import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nexapi from '../../services/api';
import { ReminderFrequencyModel } from '../../types/models/ReminderFrequencyModel';
import { notify } from '../../components/UI/notify';
import { NotifyType } from '../../types/enums/notifytype';
import {RequestStatus} from '../slices/commonEnum';

interface ReminderState {
  data: ReminderFrequencyModel[];
  totalRecords: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  status: string | null;
}

interface ReminderFrequencyResponse {
  reminderFrequency: ReminderFrequencyModel[];
  totalCount: number;
}

const initialState: ReminderState = {
  data: [],
  totalRecords: 0,
  currentPage: 0,
  loading: false,
  error: null,
  status: 'idle',
};

const API_URL = '/ReminderFrequency';
const { message } = notify();

export const fetchReminderFrequency = createAsyncThunk<ReminderFrequencyResponse, { 
  filters: Record<string, any>;  
  page: number; 
  numberOfRecord: number;
  userId: string;
  sortColumn: string;
  sortOrder: 'asc' | 'desc';
}>(
  'ReminderFrequency/fetchReminderFrequency',
  async ({ filters, page, numberOfRecord, userId, sortColumn = '', sortOrder = 'asc'  }) => {
    try {
      const response = await nexapi.post(API_URL + `/GetAll?userId=${userId}&pagination=true&page=${page}&numberOfRecord=${numberOfRecord}&search=${filters.search}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&state=${filters.state}`);
      const data = response.data;
      ////console.log(response.data);
      if (Array.isArray(data?.data)) {
        return {
          reminderFrequency: data.data, // The list of applications
          totalCount: data.totalCount, // The total count of applications
        };
      } else {
        console.error("Invalid API Response:", data);
        return { reminderFrequency: [], totalCount: 0 }; // Return a fallback structure
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      return { reminderFrequency: [], totalCount: 0 };
    }
  }
);

export const getReminderById = createAsyncThunk(
  'reminderFrequency/getReminderById',
  async ({ id }: { id: string }) => {
    const response = await nexapi.get(
      API_URL + `/${id}`
    );
    //console.log(response.data.data);
    return {
      reminderFrequency: response.data,
    };
  }
);

export const createReminderFrequency = createAsyncThunk('reminderFrequency/createReminderFrequency', async (reminderFrequency:ReminderFrequencyModel) => {
  const response = await nexapi.post(API_URL, reminderFrequency);
  message(NotifyType.Success, "ReminderFrequency created successfully")
  return response.data;
});

export const updateReminderFrequency = createAsyncThunk('reminderFrequency/updateReminderFrequency', async (reminderFrequency:ReminderFrequencyModel ) => {
  const response = await nexapi.put(`${API_URL}/${reminderFrequency.id}`, reminderFrequency);
  message(NotifyType.Success, "ReminderFrequency updated successfully")
  return response.data;
});

// export const deleteFrequency = createAsyncThunk('reminderFrequency/deleteReminderFrequency', async (id:any) => {
//   try {
//   await nexapi.delete(`/ReminderFrequency/${id}`);
//   return id;
// } catch (error) {
//   return error.message
// }
// });


export const deleteFrequency = createAsyncThunk<ReminderFrequencyResponse, { Id : string, UserId:string,reason: string}>(
  'reminderFrequency/deleteReminderFrequency',
  async ({ Id,UserId,reason}) => {
    
    try {
      const [cleanId] = Id.includes('_') ? Id.split('_') : [Id];

      const payload = {
        Id: cleanId,
        UserId: UserId,
        reason: reason
      };
      const url = API_URL+"/ActivateDeactivateReminder";
      const method = "post";
      const response = await nexapi[method](url, payload);

      
      response.data.statusCode = "200";
      return response.data.statusCode;
    } catch (error) {
      console.error('API Call Failed:', error);
      throw error;
    }
  }
);



const requestsSlice = createSlice({
  name: 'reminderFrequency',
  initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchReminderFrequency.fulfilled, (state, action) => {
          // Assuming action.payload contains applications and totalCount
          state.data = action.payload.reminderFrequency;
          state.totalRecords = action.payload.totalCount;
          state.status = RequestStatus.SUCCEEDED;
        })
        .addCase(createReminderFrequency.fulfilled, (state, action) => {
          state.data.push(action.payload);
          state.status = RequestStatus.SUCCEEDED;
        })
        .addCase(updateReminderFrequency.fulfilled, (state, action) => {
          const index = state.data.findIndex((app) => app.id === action.payload.id);
          if (index !== -1) state.data[index] = action.payload;
          state.status = RequestStatus.SUCCEEDED;
        })
        .addCase(deleteFrequency.fulfilled, (state, action:any) => {
          state.data = state.data.filter((app) => app.id !=  action.payload);
          state.status = RequestStatus.SUCCEEDED;
        })
        .addCase(getReminderById.fulfilled, (state, action) => {
          // Handling single requests data
          const reminderFrequency = action.payload.reminderFrequency;
          state.data = [reminderFrequency]; // Assuming you're replacing the entire data with a single reminider
          state.status = RequestStatus.SUCCEEDED;
        })
        .addMatcher(
          (action) => action.type.endsWith('/pending'),
          (state) => {
            state.status = RequestStatus.LOADING;
          }
        )
        .addMatcher(
          (action) => action.type.endsWith('/rejected'),
          (state, action) => {
            state.status = RequestStatus.FAILED;    
          }
        );
    },
});

export default requestsSlice.reducer;
